import { type FC } from "react";

import { Box } from "@mantine/core";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, type Discoverability, type MyLensesLensStatus } from "../../../gql-generated/types";
import { type LensPreviewMediaItem } from "../../../shared/state";
import { useGameLensState } from "../../hooks/useGameLensState";
import { type LensActionsVisibilityState } from "../../state";

import { GameLensSidebarPanel } from "./GameLensSidebarPanel";
import { LensCtaContent } from "./LensCtaContent";
import { LensDiscoverabilityTagsSidebarPanel } from "./LensDiscoverabilityTagsSidebarPanel";
import { SectionSeparator, sidebarContent } from "./LensSidebar.styles";
import { CommunityTags } from "./LensTags";
import { SpectaclesTagsSideBar } from "./SpectaclesTagsSideBar";

const BREAKPOINT_SIDEBAR = "lg";

interface LensSidebarProps {
    tags: readonly string[];
    spectaclesTags: readonly string[];
    status: MyLensesLensStatus;
    lensPreview: LensPreviewMediaItem;
    lensIconURL: string;
    lensId: string;
    discoverability: Discoverability | null;
    actionsConfig: LensActionsVisibilityState;
    primaryCategoryId?: string;
    isGameUserProvided: boolean;
    isSpectaclesOnly?: boolean;
    hasSpectacles?: boolean;
}

export const LensSidebar: FC<LensSidebarProps> = ({
    tags,
    spectaclesTags,
    lensPreview,
    lensIconURL,
    lensId,
    discoverability,
    actionsConfig,
    primaryCategoryId,
    isGameUserProvided,
    isSpectaclesOnly = false,
    hasSpectacles,
}) => {
    const { configs } = useFeatureGates();

    const isLensCtaEnabled = configs[CofConfigId.MyLensesCta].enabled;
    const isMyLensesDiscoverabilityTagsV1Enabled = configs[CofConfigId.MyLensesDiscoverabilityTagsV1].enabled;
    const isSpectaclesNewFieldsEnabled = configs[CofConfigId.MyLensesSpectaclesRnTagsMultiPreviews].enabled;
    const gameLensState = useGameLensState({
        primaryCategoryId,
        isGameUserProvided,
    });

    return (
        <Box
            className={sidebarContent}
            w={{ base: "100%", [BREAKPOINT_SIDEBAR]: 300 }}
            ml={{ base: 0, [BREAKPOINT_SIDEBAR]: "lg" }}
        >
            {isMyLensesDiscoverabilityTagsV1Enabled ? (
                <LensDiscoverabilityTagsSidebarPanel lensId={lensId} discoverability={discoverability} />
            ) : (
                <CommunityTags tags={tags} disabled={!actionsConfig.setTags} lensId={lensId} />
            )}
            {gameLensState && (
                <>
                    <SectionSeparator />
                    <GameLensSidebarPanel gameLensState={gameLensState} />
                </>
            )}
            {isSpectaclesNewFieldsEnabled && hasSpectacles && (
                // Keeping the same setTags permission as tags of the month
                <>
                    <SectionSeparator />
                    <SpectaclesTagsSideBar disabled={!actionsConfig.setTags} tags={spectaclesTags} lensId={lensId} />
                </>
            )}

            {isLensCtaEnabled && !isSpectaclesOnly && (
                <>
                    <SectionSeparator />
                    <LensCtaContent
                        lensIconURL={lensIconURL}
                        lensPreview={lensPreview}
                        disabled={!actionsConfig.setLensCta}
                        lensId={lensId}
                    />
                </>
            )}
        </Box>
    );
};
